import React, { useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'ag_cookies_consent';
const TTL_DAYS = 180;
// Evento que reabre o aviso a partir do link "Gerenciar Cookies" do rodapé,
// como prevê a Política de Privacidade (item 2.3).
export const GERENCIAR_COOKIES = 'ag:cookies:gerenciar';
const OPCIONAIS = [
  ['analytics', 'Estatística', 'Medem o uso do site, sem identificar você.'],
  [
    'preferences',
    'Preferências',
    'Guardam escolhas de exibição entre visitas.',
  ],
];

const ler = () => {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || null;
  } catch {
    return null;
  }
};

const isExpired = (dados) =>
  !dados || !dados.ts || Date.now() - dados.ts > TTL_DAYS * 24 * 3600 * 1000;

// Evento disparado sempre que o visitante grava uma escolha no aviso (aceitar,
// recusar ou personalizar). Quem depende do consentimento, como a estatística
// de uso, escuta este evento em vez de ler o navegador só na carga da página.
export const CONSENTIMENTO_ALTERADO = 'ag:cookies:consentimento';

// O visitante aceitou a categoria "Estatística" e a escolha ainda vale?
export const consentiuEstatistica = () => {
  const dados = ler();
  return !isExpired(dados) && (dados.categories || []).includes('analytics');
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [personalizar, setPersonalizar] = useState(false);
  const [escolhas, setEscolhas] = useState({});
  // Foco: quem reabre o aviso pelo rodapé vai direto às escolhas, e o foco
  // volta ao rodapé quando o aviso fecha (o aviso fica no começo do DOM).
  const escolhasRef = useRef(null);
  const origemRef = useRef(null);
  const [focar, setFocar] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (isExpired(ler())) setVisible(true);
    const abrir = () => {
      const atuais = (ler() || {}).categories || [];
      setEscolhas(
        Object.fromEntries(OPCIONAIS.map(([k]) => [k, atuais.includes(k)])),
      );
      origemRef.current = document.activeElement;
      setPersonalizar(true);
      setVisible(true);
      setFocar(true);
    };
    window.addEventListener(GERENCIAR_COOKIES, abrir);
    return () => window.removeEventListener(GERENCIAR_COOKIES, abrir);
  }, []);

  useEffect(() => {
    if (!visible || !focar || !escolhasRef.current) return;
    const primeira = escolhasRef.current.querySelector('input:not([disabled])');
    if (primeira) primeira.focus();
    setFocar(false);
  }, [visible, focar]);

  const setConsent = (categories) => {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ts: Date.now(), categories }),
    );
    window.dispatchEvent(
      new CustomEvent(CONSENTIMENTO_ALTERADO, { detail: { categories } }),
    );
    setVisible(false);
    setPersonalizar(false);
    if (origemRef.current && origemRef.current.focus) {
      origemRef.current.focus();
      origemRef.current = null;
    }
  };

  const salvarEscolhas = () =>
    setConsent([
      'essential',
      ...OPCIONAIS.map(([k]) => k).filter((k) => escolhas[k]),
    ]);

  if (!visible) return null;

  return (
    <div
      className="ag-cookie-banner"
      role="dialog"
      aria-label="Aviso de cookies"
    >
      <div className="ag-cookie-banner__inner">
        <div className="ag-cookie-banner__text">
          <strong className="ag-cookie-banner__title">
            <span aria-hidden="true">⚪</span> Sua privacidade é importante
          </strong>
          <p>
            Usamos cookies essenciais para o funcionamento do site, conforme a{' '}
            <strong>LGPD</strong>. Cookies opcionais ajudam a melhorar a
            experiência — você decide quais aceitar.{' '}
            <a href="/institucional/politica-de-privacidade">Saiba mais</a>.
          </p>
          {personalizar && (
            <fieldset className="ag-cookie-banner__escolhas" ref={escolhasRef}>
              <legend>Escolha os cookies opcionais</legend>
              <label className="ag-cookie-banner__opcao">
                <input type="checkbox" checked disabled />
                <span>
                  <b>Essenciais</b> Necessários para o site funcionar. Sempre
                  ativos.
                </span>
              </label>
              {OPCIONAIS.map(([k, rotulo, ajuda]) => (
                <label className="ag-cookie-banner__opcao" key={k}>
                  <input
                    type="checkbox"
                    checked={!!escolhas[k]}
                    onChange={(e) =>
                      setEscolhas({ ...escolhas, [k]: e.target.checked })
                    }
                  />
                  <span>
                    <b>{rotulo}</b> {ajuda}
                  </span>
                </label>
              ))}
            </fieldset>
          )}
        </div>
        <div className="ag-cookie-banner__actions">
          {personalizar ? (
            <button
              type="button"
              className="ag-cookie-banner__btn ag-cookie-banner__btn--secondary"
              onClick={salvarEscolhas}
            >
              Salvar escolhas
            </button>
          ) : (
            <button
              type="button"
              className="ag-cookie-banner__btn ag-cookie-banner__btn--text"
              onClick={() => setPersonalizar(true)}
            >
              Personalizar
            </button>
          )}
          <button
            type="button"
            className="ag-cookie-banner__btn ag-cookie-banner__btn--secondary"
            onClick={() => setConsent(['essential'])}
          >
            Apenas essenciais
          </button>
          <button
            type="button"
            className="ag-cookie-banner__btn ag-cookie-banner__btn--primary"
            onClick={() =>
              setConsent(['essential', 'analytics', 'preferences'])
            }
          >
            Aceitar todos
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
